/**
 * The counts and percentage on the parent Attendance screens, worked out from the day
 * records actually on screen (this week's for Weekly, the month's otherwise).
 *
 * They used to be the server's month totals, which counted every leave day shown (5) but
 * only register days as "Total days" (1) and in the percentage (100%) - so the counts
 * overlapped, did not add up, and the Weekly tab showed the month's figures under the
 * week's bars (QA). The rule the school chose: approved leave is excluded from the
 * percentage; it is still counted and shown.
 */
export interface AttendanceDayLike {
  status?: string | null;
  /** 0-1: share of the day's sittings attended (leave is 0). */
  attendanceFraction?: number | null;
}

export interface AttendanceTotals {
  /** Present and late days - in school. */
  present: number;
  /** Attended from home - its own row, not also inside present. */
  remote: number;
  halfDay: number;
  absent: number;
  leave: number;
  /** Every day with a record: present + remote + half day + absent + leave. */
  totalDays: number;
  /** The days the percentage is out of: total less leave. */
  countedDays: number;
  /** Rounded; null when there is no day to count (e.g. a week entirely on leave). */
  percentage: number | null;
}

export const LEAVE_EXCLUDED_NOTE = 'Approved leave is excluded from attendance percentage.';

const ATTENDED = new Set(['present', 'late', 'remote']);

/** How much of a day counts as attended: the server's fraction, else 1 / ½ / 0 by status. */
function attendedShare(status: string, fraction: number | null | undefined): number {
  if (typeof fraction === 'number' && fraction >= 0 && fraction <= 1) return fraction;
  if (ATTENDED.has(status)) return 1;
  if (status === 'half day') return 0.5;
  return 0;
}

export function attendanceTotals(days: AttendanceDayLike[] | null | undefined): AttendanceTotals {
  let present = 0;
  let remote = 0;
  let halfDay = 0;
  let absent = 0;
  let leave = 0;
  let attended = 0;

  for (const day of days ?? []) {
    const status = String(day?.status ?? '').trim().toLowerCase();
    if (status === 'leave') {
      leave++;
      continue;
    }
    if (status === 'present' || status === 'late') present++;
    else if (status === 'remote') remote++;
    else if (status === 'half day') halfDay++;
    else if (status === 'absent') absent++;
    else continue; // not a recorded day
    attended += attendedShare(status, day.attendanceFraction);
  }

  const countedDays = present + remote + halfDay + absent;
  return {
    present,
    remote,
    halfDay,
    absent,
    leave,
    totalDays: countedDays + leave,
    countedDays,
    percentage: countedDays === 0 ? null : Math.round((100 * attended) / countedDays),
  };
}
